import { parseUserAgent, UserAgentInfo } from './user-agent';

/** Mismo separador que pwcs (`device-label.util` / `EnrichedUserAgent::SEPARATOR`). */
export const ENRICHED_USER_AGENT_SEPARATOR = ' | ';

/** Fallback de Client Hints en journey cuando no hay `model` real (pwcs `resolveDeviceLabelFromNavigator`). */
const GENERIC_DEVICE_LABEL_PATTERN = /^.+\s+\((?:Generic Mobile|Desktop)\)$/i;

/**
 * Etiquetas como «Android (Generic Mobile)» o «Windows (Desktop)» no son modelo de dispositivo.
 */
export function isGenericDeviceLabel(label: string | null | undefined): boolean {
	const trimmed = label?.trim() ?? '';
	if (trimmed === '') {
		return true;
	}
	return GENERIC_DEVICE_LABEL_PATTERN.test(trimmed);
}

/**
 * Devuelve el modelo solo si el prefijo del UA enriquecido es un modelo real (no fallback genérico).
 */
export function resolveDeviceModelFromLabel(label: string | null | undefined): string | null {
	const trimmed = label?.trim() ?? '';
	if (trimmed === '' || isGenericDeviceLabel(trimmed)) {
		return null;
	}
	return trimmed;
}

export interface SplitEnrichedUserAgentResult {
	deviceModel: string | null;
	nativeUserAgent: string;
}

/**
 * Separa etiqueta de dispositivo y UA nativo almacenados en `process_devices.user_agent`.
 */
export function splitEnrichedUserAgent(stored: string | null | undefined): SplitEnrichedUserAgentResult {
	const value = (stored ?? '').trim();
	if (value === '') {
		return { deviceModel: null, nativeUserAgent: '' };
	}

	const separator = ENRICHED_USER_AGENT_SEPARATOR;
	const index = value.indexOf(separator);
	if (index === -1) {
		return { deviceModel: null, nativeUserAgent: value };
	}

	const label = value.slice(0, index).trim();
	const native = value.slice(index + separator.length).trim();

	if (label === '') {
		return { deviceModel: null, nativeUserAgent: value };
	}

	if (native === '') {
		return { deviceModel: label, nativeUserAgent: '' };
	}

	return { deviceModel: label, nativeUserAgent: native };
}

export interface ProcessDeviceUserAgentFields {
	user_agent?: string;
	user_agent_info?: UserAgentInfo;
	device_model?: string;
}

/** Parsea UA enriquecido de BD: modelo (prefijo) + info de navegador sobre UA nativo. */
export function applyProcessDeviceUserAgentParsing(device: ProcessDeviceUserAgentFields): void {
	const { deviceModel, nativeUserAgent } = splitEnrichedUserAgent(device.user_agent);
	const resolvedModel = resolveDeviceModelFromLabel(deviceModel);
	if (resolvedModel) {
		device.device_model = resolvedModel;
	}
	const uaForParsing = nativeUserAgent || device.user_agent || '';
	if (uaForParsing) {
		device.user_agent_info = parseUserAgent(uaForParsing);
	}
}
